import { ParamMap, Params } from '@angular/router';
import { SpsPruefstatus, SpsSignalFilter, SpsSignalRichtung, SpsSignalSortierung } from './sps-signal.model';
import { anlagenteilAuswahlLesen, anlagenteilAuswahlWert } from './sps-listen-filter.model';

/*
 * Filterzustand der SPS-Prüfung ↔ URL-Query-Parameter. Damit überleben
 * Richtung, Suche, Statusfilter, Anlagenteil, Sortierung und Seite den
 * Wechsel in die Signaldetailansicht und zurück, und die Detailansicht kann
 * „Vorheriges / Nächstes“ in derselben Menge bestimmen.
 */

const RICHTUNGEN: SpsSignalRichtung[] = ['Eingang', 'Ausgang', 'Unbekannt'];
const STATUS: SpsPruefstatus[] = ['Ungeprueft', 'Ok', 'NichtOk', 'Reserve'];
const SORTIERUNGEN: SpsSignalSortierung[] = [
  'adresse', 'adresse_desc', 'name', 'name_desc', 'status', 'status_desc', 'station', 'station_desc', 'geprueft', 'geprueft_desc'
];

export const SPS_FILTER_STANDARD_GROESSE = 50;

export function leererSpsSignalFilter(projektId: number): SpsSignalFilter {
  return {
    projektId,
    richtung: 'Eingang',
    status: null,
    suche: '',
    name: '',
    adresse: '',
    bmk: '',
    beschreibung: '',
    nurOffen: false,
    nurUngeklaert: false,
    nurGeaendertSeitPruefung: false,
    anlagenteilId: null,
    ohneAnlagenteil: false,
    seite: 1,
    groesse: SPS_FILTER_STANDARD_GROESSE,
    sortierung: 'adresse'
  };
}

/** Filter aus Query-Parametern; fehlende Werte bekommen den Standard. */
export function spsFilterAusParams(params: ParamMap, projektId: number): SpsSignalFilter {
  const f = leererSpsSignalFilter(projektId);

  const richtung = params.get('richtung');
  if (richtung === 'alle') {
    f.richtung = null;
  } else if (RICHTUNGEN.includes(richtung as SpsSignalRichtung)) {
    f.richtung = richtung as SpsSignalRichtung;
  }

  const status = params.get('status');
  if (STATUS.includes(status as SpsPruefstatus)) {
    f.status = status as SpsPruefstatus;
  }

  const sortierung = params.get('sortierung');
  if (SORTIERUNGEN.includes(sortierung as SpsSignalSortierung)) {
    f.sortierung = sortierung as SpsSignalSortierung;
  }

  f.suche = params.get('suche') ?? '';
  f.name = params.get('name') ?? '';
  f.adresse = params.get('adresse') ?? '';
  f.bmk = params.get('bmk') ?? '';
  f.beschreibung = params.get('beschreibung') ?? '';
  f.nurOffen = params.get('nurOffen') === 'true';
  f.nurUngeklaert = params.get('nurUngeklaert') === 'true';
  f.nurGeaendertSeitPruefung = params.get('nurGeaendert') === 'true';

  const anlagenteil = anlagenteilAuswahlLesen(params.get('anlagenteil'));
  f.anlagenteilId = anlagenteil.anlagenteilId;
  f.ohneAnlagenteil = anlagenteil.ohneAnlagenteil;

  const seite = Number(params.get('seite'));
  if (Number.isFinite(seite) && seite >= 1) {
    f.seite = Math.floor(seite);
  }

  const groesse = Number(params.get('groesse'));
  if (Number.isFinite(groesse) && groesse >= 1 && groesse <= 500) {
    f.groesse = Math.floor(groesse);
  }

  return f;
}

/** Query-Parameter eines Filters – nur gesetzte Werte, damit die URL lesbar bleibt. */
export function spsFilterAlsParams(filter: SpsSignalFilter): Params {
  const p: Params = {};

  p['richtung'] = filter.richtung ?? 'alle';
  if (filter.status) { p['status'] = filter.status; }
  if (filter.suche) { p['suche'] = filter.suche; }
  if (filter.name) { p['name'] = filter.name; }
  if (filter.adresse) { p['adresse'] = filter.adresse; }
  if (filter.bmk) { p['bmk'] = filter.bmk; }
  if (filter.beschreibung) { p['beschreibung'] = filter.beschreibung; }
  if (filter.nurOffen) { p['nurOffen'] = 'true'; }
  if (filter.nurUngeklaert) { p['nurUngeklaert'] = 'true'; }
  if (filter.nurGeaendertSeitPruefung) { p['nurGeaendert'] = 'true'; }

  const anlagenteil = anlagenteilAuswahlWert(filter.anlagenteilId, filter.ohneAnlagenteil);
  if (anlagenteil != null) { p['anlagenteil'] = anlagenteil; }

  if (filter.sortierung && filter.sortierung !== 'adresse') { p['sortierung'] = filter.sortierung; }
  if ((filter.seite ?? 1) > 1) { p['seite'] = filter.seite; }
  if (filter.groesse && filter.groesse !== SPS_FILTER_STANDARD_GROESSE) { p['groesse'] = filter.groesse; }

  return p;
}

/** True, wenn ein Signal mit diesem Prüfstatus (noch) zum Filter passt. */
export function passtZumStatusFilter(filter: SpsSignalFilter, status: SpsPruefstatus): boolean {
  if (filter.status && filter.status !== status) {
    return false;
  }

  if (filter.nurOffen && status !== 'Ungeprueft' && status !== 'NichtOk') {
    return false;
  }

  return true;
}
